import React from 'react';
import SEO from '../components/SEO';
import { seoData } from '../data/seo';
import { Users, Target, Lightbulb, Award } from 'lucide-react';
import { Card } from '../components/ui/card';
import { teamMembers, companyInfo } from '../data/mock';

const About = () => {
  return (
    <div className="min-h-screen">
      <SEO {...seoData.about} />
      {/* Hero Section */}
      <section className="pt-32 pb-20 px-4 sm:px-6 lg:px-8 bg-gradient-to-br from-gray-50 to-white">
        <div className="max-w-4xl mx-auto text-center">
          <h1 className="text-5xl sm:text-6xl font-bold text-gray-900 mb-6">
            About <span className="text-cyan-500">Digital Studio</span>
          </h1>
          <p className="text-xl text-gray-600 leading-relaxed">
            {companyInfo.description}
          </p>
        </div>
      </section>

      {/* Team Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">Meet Our Team</h2>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">
              Three passionate professionals dedicated to your success
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {teamMembers.map((member) => (
              <Card key={member.id} className="p-8 text-center hover:shadow-lg transition-all duration-300 rounded-lg">
                <div className="w-24 h-24 bg-gradient-to-br from-cyan-400 to-cyan-600 rounded-full mx-auto mb-6 flex items-center justify-center">
                  <span className="text-3xl font-bold text-white">
                    {member.name.charAt(0)}
                  </span>
                </div>
                <h3 className="text-2xl font-semibold text-gray-900 mb-2">
                  {member.name}
                </h3>
                <p className="text-cyan-500 font-medium mb-4">{member.role}</p>
                <p className="text-gray-600 leading-relaxed">{member.bio}</p>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Values Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-gray-50">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">Our Approach</h2>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">
              What makes us different
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            <Card className="p-6 text-center rounded-lg">
              <div className="w-16 h-16 bg-cyan-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <Users className="w-8 h-8 text-cyan-500" />
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-3">
                Client-Focused
              </h3>
              <p className="text-gray-600">
                Your success is our success. We listen, understand, and deliver.
              </p>
            </Card>

            <Card className="p-6 text-center rounded-lg">
              <div className="w-16 h-16 bg-cyan-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <Target className="w-8 h-8 text-cyan-500" />
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-3">
                Results-Driven
              </h3>
              <p className="text-gray-600">
                We focus on measurable outcomes that drive real business growth.
              </p>
            </Card>

            <Card className="p-6 text-center rounded-lg">
              <div className="w-16 h-16 bg-cyan-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <Lightbulb className="w-8 h-8 text-cyan-500" />
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-3">
                Creative Solutions
              </h3>
              <p className="text-gray-600">
                Fresh ideas and innovative approaches for every project.
              </p>
            </Card>

            <Card className="p-6 text-center rounded-lg">
              <div className="w-16 h-16 bg-cyan-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <Award className="w-8 h-8 text-cyan-500" />
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-3">
                Quality First
              </h3>
              <p className="text-gray-600">
                Every detail matters. We deliver excellence in everything we do.
              </p>
            </Card>
          </div>
        </div>
      </section>

      {/* Skills Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-white">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">Our Skills & Tools</h2>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">
              Modern technologies and platforms we work with
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <Card className="p-6 rounded-lg">
              <h3 className="text-xl font-semibold text-gray-900 mb-4">Web Development</h3>
              <ul className="space-y-2 text-gray-600">
                <li>• React & Modern JavaScript</li>
                <li>• Responsive Design</li>
                <li>• WordPress & CMS</li>
                <li>• SEO Optimization</li>
                <li>• E-commerce Solutions</li>
              </ul>
            </Card>

            <Card className="p-6 rounded-lg">
              <h3 className="text-xl font-semibold text-gray-900 mb-4">Social Media</h3>
              <ul className="space-y-2 text-gray-600">
                <li>• Instagram Marketing</li>
                <li>• Facebook Ads</li>
                <li>• Content Creation</li>
                <li>• Community Management</li>
                <li>• Analytics & Reporting</li>
              </ul>
            </Card>

            <Card className="p-6 rounded-lg">
              <h3 className="text-xl font-semibold text-gray-900 mb-4">Design & Branding</h3>
              <ul className="space-y-2 text-gray-600">
                <li>• UI/UX Design</li>
                <li>• Brand Identity</li>
                <li>• Visual Content</li>
                <li>• Figma & Adobe Suite</li>
                <li>• Design Systems</li>
              </ul>
            </Card>
          </div>
        </div>
      </section>
    </div>
  );
};

export default About;
